import { isNil } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { Dropdown, DropdownButton } from '@shared/components/dropdown';
import { List } from '@shared/components/filter/List';
import { TagOptions } from '@shared/components/filter/filterOptions/TagOptions';
import { ClosedView } from '@shared/components/filter/filterOptions/multiSelect/MultiSelect';
import { mapFilterByComponentToState } from '@shared/components/filter/state/mapCollectionCriteriaToState';
import {
  ControlOption,
  FilterOperation,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
} from '@shared/components/filter/types';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextBody1Bold, MBTextBody2 } from '@shared/components/typography';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import {
  AutoExtendConditionType,
  AutoExtendOptionsProps,
  RepeatEndType,
} from '@shared/scenes/entity/components/fields/DateEditField/types';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { useConditionTypeOptions } from './hooks/useConditionTypeOptions';
import { MAX_COUNT, getInitialAutoExtendTagFilterOperator, getOperatorLabel } from './utils';

export function AutoExtendOptionsDesktop({
  minimumInSeries,
  setMinimumInSeries,
  tagFilter,
  setTagFilter,
  component,
  workflowTemplateId,
  conditionType,
  setConditionType,
  setRepeatEndType,
}: AutoExtendOptionsProps) {
  const hasDefaultAutoExtend = !isNil(component?.autoExtendDefault?.impliedFilterComponentId);
  const showConditionTypeMenu = hasDefaultAutoExtend;

  const handleSetConditionType = (newType?: AutoExtendConditionType) => {
    setConditionType(newType);

    if (newType === 'default') {
      setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? MAX_COUNT);
      setTagFilter({
        componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
      setRepeatEndType(RepeatEndType.NEVER);
    } else if (newType === 'custom') {
      setMinimumInSeries(MAX_COUNT);
      setTagFilter(undefined);
    } else if (newType === undefined) {
      setMinimumInSeries(undefined);
      setTagFilter(undefined);
      setRepeatEndType(RepeatEndType.END_DATE);
    }
  };

  return (
    <>
      <Divider style={{ marginTop: grid(1.5), marginLeft: grid(1.5), width: grid(34) }} />
      <View paddingHorizontal={2} paddingBottom={2} paddingTop={1}>
        <MBTextBody1Bold style={{ paddingBottom: grid(1) }}>
          {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.title')}
        </MBTextBody1Bold>
        {showConditionTypeMenu && (
          <ConditionTypeMenu
            conditionType={conditionType}
            setConditionType={handleSetConditionType}
            component={component}
          />
        )}
        {conditionType === 'custom' && (
          <>
            <MinimumInSeries
              minimumInSeries={minimumInSeries}
              setMinimumInSeries={setMinimumInSeries}
            />
            <TagFilter
              tagFilter={tagFilter}
              setTagFilter={setTagFilter}
              workflowTemplateId={workflowTemplateId}
            />
          </>
        )}
      </View>
    </>
  );
}

function ConditionTypeMenu({
  conditionType,
  setConditionType,
  component,
}: {
  conditionType?: AutoExtendConditionType;
  setConditionType: (conditionType?: AutoExtendConditionType) => void;
  component: MBAPI.WorkflowTemplate_components_DateComponent;
}) {
  const availableOptions = useConditionTypeOptions({
    component,
  });
  const hasMultipleOptions = availableOptions.length > 1;
  const isCustomOptionSelected = conditionType === 'custom';

  useEffect(() => {
    if (!hasMultipleOptions) {
      setConditionType(availableOptions[0].id as AutoExtendConditionType);
    }
  }, [availableOptions, hasMultipleOptions, setConditionType]);

  const selectedOption = useMemo(() => {
    return availableOptions.find(option => option.id === conditionType);
  }, [conditionType, availableOptions]);

  const handleConditionTypeChange = (option: ControlOption) => {
    const nextConditionType = option.id as AutoExtendConditionType;
    setConditionType(nextConditionType === conditionType ? undefined : nextConditionType);
  };

  if (!hasMultipleOptions) {
    return null;
  }

  return (
    <Dropdown
      button={({ onClick, isOpen }) => (
        <DropdownButton
          onClick={onClick}
          open={isOpen}
          style={{ marginBottom: isCustomOptionSelected ? grid(1.5) : 0 }}
        >
          <View row centerVertically style={{ maxWidth: '90%' }}>
            <MBTextBody1
              numberOfLines={1}
              color={!isNil(selectedOption) ? Colors.v2.purple : Colors.v2.gray50}
            >
              {selectedOption?.label ??
                i18n('workflowTemplate.component.dateAndRepeat.autoExtend.setConditionPlaceholder')}
            </MBTextBody1>
          </View>
        </DropdownButton>
      )}
      closeOnSelect
    >
      <List
        options={availableOptions}
        selected={selectedOption}
        showSearch={false}
        onSelect={option => handleConditionTypeChange(option)}
      />
    </Dropdown>
  );
}

const MIN_COUNT = 1;

function MinimumInSeries({
  minimumInSeries,
  setMinimumInSeries,
}: {
  minimumInSeries?: number;
  setMinimumInSeries: (minimumInSeries?: number) => void;
}) {
  const countIsOutOfRange =
    isNil(minimumInSeries) || minimumInSeries < MIN_COUNT || minimumInSeries > MAX_COUNT;

  const handleMinimumInSeriesChange = (newCount: string) => {
    if (newCount === '') {
      setMinimumInSeries(undefined);
      return;
    }
    const numCount = Number(newCount);
    const isNumber = !isNaN(numCount);
    if (isNumber) {
      setMinimumInSeries(numCount);
    }
  };

  return (
    <View paddingBottom={1}>
      <View row centerVertically>
        <MBTextInput
          value={String(minimumInSeries ?? MAX_COUNT)}
          onChangeText={handleMinimumInSeriesChange}
          style={[styles.input, countIsOutOfRange && styles.error]}
          placeholder="1-100"
        />
        <View paddingLeft={1}>
          <MBTextBody2>
            {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.minimumInSeries', {
              count: minimumInSeries ?? 0,
            })}
          </MBTextBody2>
        </View>
      </View>
    </View>
  );
}

export const tagFilterOperators = [
  {
    label: i18n('card.actions.filters.fields.common.operator.autoContains'),
    id: 'contains',
  },
  {
    label: i18n('card.actions.filters.fields.common.operator.doesNotContainMenu'),
    id: 'does-not-contain',
  },
];

export function TagFilter({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
  maxTagDropdownWidth = 220,
}: {
  tagFilter?: MBAPI.FilterByComponent;
  setTagFilter: (tagFilter?: MBAPI.FilterByComponent) => void;
  workflowTemplateId: string;
  maxTagDropdownWidth?: number;
}) {
  const [selectedOperator, setSelectedOperator] = useState<string>(
    getInitialAutoExtendTagFilterOperator(tagFilter),
  );
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const tagComponents = workflowTemplate?.components.filter(isTagComponent);
  const tagComponentOptions: ControlOption[] = (tagComponents ?? []).map(component => ({
    id: component.id,
    label: component.label ?? '',
    icon: component.icon as MBIconV2Name,
  }));
  const selectedTagComponentOption = tagComponentOptions?.find(
    to => to.id === tagFilter?.componentId,
  );
  const selectedTagComponent = tagComponents?.find(c => c.id === tagFilter?.componentId);

  const isMultiSelect = selectedTagComponent?.multiselect;
  const showTagValue = selectedOperator === 'does-not-contain';

  const operatorLabel = getOperatorLabel(selectedOperator);

  const handleSelectOperator = (option: ControlOption) => {
    if (option.id === 'contains') {
      setSelectedOperator('contains');
      const newTagFilter: MBAPI.FilterByComponent = {
        componentId: selectedTagComponent?.id ?? tagFilter?.componentId,
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      } as MBAPI.FilterByComponent;

      setTagFilter(newTagFilter);
    } else if (option.id === 'does-not-contain') {
      setSelectedOperator('does-not-contain');
      const newTagFilter: MBAPI.FilterByComponent = {
        componentId: selectedTagComponent?.id ?? tagFilter?.componentId,
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      } as MBAPI.FilterByComponent;
      setTagFilter(newTagFilter);
    }
  };

  const filterUuidRef = useRef<string>(uuidv4());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }
    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label,
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    } as ControlOption;
  }, [selectedTagComponent]);

  const selectedOptions = useMemo(() => {
    if (!filterOption) {
      return new Map<string, FilterSelectionState>();
    }

    const hasTagValues = Boolean(tagFilter?.value?.containsString?.values?.length);

    if (!hasTagValues) {
      return new Map<string, FilterSelectionState>();
    }

    const filterArray = tagFilter ? [tagFilter] : [];
    return mapFilterByComponentToState(filterArray, [filterOption]);
  }, [tagFilter, filterOption]);

  const handleTagComponentOptionChange = (option: ControlOption) => {
    // If the user selects the same tag component that is already selected, deselect it.
    if (option.id === tagFilter?.componentId) {
      setTagFilter(undefined);
      return;
    }

    const newFilter: MBAPI.FilterByComponent = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as MBAPI.FilterByComponent;

    setTagFilter(newFilter);
  };

  const handleSetOption = (
    _filter: ControlOption,
    operation: FilterOperation,
    selection?: FilterSelection,
  ) => {
    if (operation !== 'updateSelections' || !selectedTagComponent) {
      return;
    }

    const selections = selection as FilterTagOption[];
    const newFilterState: FilterSelectionState = {
      filter: filterOption,
      selection: selections as unknown as FilterSelection,
      negate: selectedOperator === 'does-not-contain',
    };

    const newTagFilter = getFilterComponent(newFilterState);
    if (newTagFilter) {
      setTagFilter(newTagFilter);
    }
  };

  const currentSelections = useMemo(() => {
    if (selectedOptions.size === 0) {
      return undefined;
    }
    return Array.from(selectedOptions.values())[0]?.selection as FilterSelection;
  }, [selectedOptions]);

  const ItemComponent = ({ selection }: { selection: FilterTagOption }) => {
    if (!selection?.label) {
      return null;
    }
    return (
      <Tag
        color={selection.backgroundColor}
        label={selection.label}
        archived={selection.archived}
        style={{ maxWidth: grid(24) }}
      />
    );
  };

  return (
    <View
      style={{
        backgroundColor: Colors.v2.backgroundGrayLight,
        borderRadius: grid(1),
        borderWidth: 1,
        borderColor: Colors.v2.gray10,
        borderStyle: 'solid',
      }}
      padding={1}
    >
      <View row centerVertically flex>
        <View flex>
          <Dropdown
            button={({ onClick, isOpen }) => (
              <DropdownButton onClick={onClick} open={isOpen}>
                <View row centerVertically style={{ maxWidth: '78%' }}>
                  {selectedTagComponentOption?.icon && (
                    <MBIconV2
                      name={selectedTagComponentOption.icon as MBIconV2Name}
                      color={Colors.v2.purple}
                      size={grid(3)}
                      style={{ marginRight: grid(1) }}
                    />
                  )}
                  <MBTextBody1
                    numberOfLines={1}
                    color={!isNil(selectedTagComponentOption) ? Colors.v2.purple : Colors.v2.gray50}
                  >
                    {selectedTagComponentOption?.label || i18n('card.actions.groups.noneSelected')}
                  </MBTextBody1>
                </View>
              </DropdownButton>
            )}
            closeOnSelect
          >
            <List
              options={tagComponentOptions}
              selected={selectedTagComponentOption}
              onSelect={handleTagComponentOptionChange}
            />
          </Dropdown>
        </View>
      </View>
      {/* Operator dropdown */}
      {!isNil(filterOption) && (
        <Dropdown
          button={({ onClick, isOpen }) => (
            <DropdownButton onClick={onClick} open={isOpen} style={{ marginTop: grid(1) }}>
              <MBTextBody1 color={Colors.v2.purple}>{operatorLabel}</MBTextBody1>
            </DropdownButton>
          )}
          closeOnSelect
        >
          <List
            options={tagFilterOperators as ControlOption[]}
            onSelect={handleSelectOperator}
            showSearch={false}
          />
        </Dropdown>
      )}

      {filterOption && showTagValue && (
        <Dropdown
          key={filterOption.id}
          button={({ onClick, isOpen }) => (
            <DropdownButton
              onClick={onClick}
              open={isOpen}
              style={{ paddingLeft: 0, marginTop: grid(1) }}
            >
              <ClosedView
                selections={(currentSelections as FilterTagOption[]) || []}
                ItemComponent={ItemComponent}
                maxWidth={maxTagDropdownWidth}
              />
            </DropdownButton>
          )}
          style={{ minWidth: 300 }}
        >
          <TagOptions
            filter={filterOption}
            setOption={handleSetOption}
            selectedOptions={selectedOptions}
            includeEmptyOption={false}
            isMultiSelect={!!isMultiSelect}
          />
        </Dropdown>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  input: {
    width: grid(8),
    borderWidth: 1,
    borderColor: Colors.v2.gray10,
    borderStyle: 'solid',
    borderRadius: grid(1),
  },
  error: {
    borderColor: Colors.v2.red,
  },
});
